import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import BuildingSwitcher from './BuildingSwitcher'
import DevAccountSwitcher from './DevAccountSwitcher'
import {
  BarChart3,
  Building2,
  Users,
  Gamepad2,
  Mail,
  User,
  ChevronLeft,
  ChevronRight,
  FileImage,
  MessageSquare,
  Package,
  Smartphone,
  Flag,
  Key,
  Shield,
  ShieldCheck,
  TreePalm,
  Car,
  BookOpen,
  Bug,
  ClipboardList,
  Monitor,
  Cpu,
  BarChart,
  Bell,
  Plug
} from 'lucide-react'

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const location = useLocation()
  const { user, logout, isAdmin, isBuildingManager, userRole } = useAuth()

  const navSections = [
    {
      items: [
        { name: 'Dashboard', href: '/', icon: BarChart3, access: 'all' },
        { name: 'Buildings', href: '/buildings', icon: Building2, access: 'all' },
        { name: 'Announcements', href: '/announcements', icon: MessageSquare, access: 'all' },
        { name: 'Deliveries', href: '/deliveries', icon: Package, access: 'all' },
        { name: 'Amenities', href: '/amenities', icon: TreePalm, access: 'all' },
        { name: 'Parking', href: '/parking', icon: Car, access: 'all' },
        { name: 'Knowledge Base', href: '/knowledge-base', icon: BookOpen, access: 'all' },
        { name: 'License', href: '/license', icon: ShieldCheck, access: 'all' },
      ],
    },
    {
      label: 'ADMINISTRATION',
      access: 'admin',
      items: [
        { name: 'Static Assets', href: '/static-assets', icon: FileImage, access: 'admin' },
        { name: 'License Plans', href: '/license-plans', icon: Key, access: 'admin' },
        { name: 'Building Licenses', href: '/building-licenses', icon: Shield, access: 'admin' },
        { name: 'License Codes', href: '/license-codes', icon: BookOpen, access: 'admin' },
        { name: 'Users', href: '/users', icon: Users, access: 'admin' },
        { name: 'Notifications', href: '/notifications', icon: Bell, access: 'admin' },
        { name: 'Devices', href: '/device-installations', icon: Monitor, access: 'admin' },
      ],
    },
    {
      label: 'DEVELOPER TOOLS',
      access: 'admin',
      items: [
        { name: 'Sentry Issues', href: '/sentry-issues', icon: Bug, access: 'admin' },
        { name: 'AI Models', href: '/ai-models', icon: Cpu, access: 'admin' },
        { name: 'AI Usage', href: '/ai-usage', icon: BarChart, access: 'admin' },
        { name: 'Tasks', href: '/tasks', icon: ClipboardList, access: 'admin' },
        { name: 'Feature Flags', href: '/feature-flags', icon: Flag, access: 'admin' },
        { name: 'MCP Settings', href: '/mcp-settings', icon: Plug, access: 'admin' },
        { name: 'Demo Generator', href: '/demo', icon: Gamepad2, access: 'admin' },
      ],
    },
    {
      items: [
        { name: 'Profile', href: '/profile', icon: User, access: 'all' },
      ],
    },
  ]

  const filteredSections = navSections
    .filter(section => !section.access || section.access === 'all' || isAdmin)
    .map(section => ({
      ...section,
      items: section.items.filter(item => item.access === 'all' || isAdmin),
    }))
    .filter(section => section.items.length > 0)

  const roleLabel = isAdmin ? 'Admin' : isBuildingManager ? 'Manager' : ''

  // Name-only tokens carry no first/last name, so fall back the way the header button does.
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.name || 'Admin'

  return (
    <div className="flex h-screen bg-slate-50 font-sans">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-dark-900 border-r border-dark-800 flex flex-col transition-all duration-300 ease-in-out relative z-20`}>
        <div className="p-5 flex items-center justify-between border-b border-white/5">
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'hidden'}`}>
            <img src="/logo.png" alt="Reziland" className="h-8 w-auto" />
            <h1 className="font-display text-xl font-bold tracking-tight text-white">
              Reziland<span className="text-primary-500">.</span>
            </h1>
          </div>
          {!sidebarOpen && (
            <img src="/logo.png" alt="Reziland" className="w-8 h-8 mx-auto" />
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-md hover:bg-white/10 text-dark-300 hover:text-white transition-colors ml-auto flex-shrink-0"
          >
            {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 custom-scrollbar">
          {filteredSections.map((section, sIdx) => (
            <div key={sIdx} className={sIdx > 0 ? 'mt-2' : ''}>
              {section.label && sidebarOpen && (
                <div className="text-[0.65rem] font-semibold text-dark-500 uppercase tracking-wider px-3 pt-4 pb-1">
                  {section.label}
                </div>
              )}
              {section.label && !sidebarOpen && (
                <div className="border-t border-white/5 mx-2 mt-3 mb-2" />
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = item.href === '/'
                    ? location.pathname === item.href
                    : location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 group ${isActive
                          ? 'bg-primary-500/10 text-primary-500'
                          : 'text-dark-300 hover:bg-white/5 hover:text-white'
                        }`}
                      title={!sidebarOpen ? item.name : undefined}
                    >
                      <item.icon
                        size={20}
                        className={`flex-shrink-0 transition-colors ${isActive ? 'text-primary-500' : 'text-dark-400 group-hover:text-dark-200'}`}
                      />
                      {sidebarOpen && (
                        <span className={`ml-3 text-sm font-medium whitespace-nowrap ${isActive ? 'text-primary-500' : ''}`}>
                          {item.name}
                        </span>
                      )}
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer (User Mini Profile) */}
        {sidebarOpen && (
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-primary-600 to-primary-400 flex items-center justify-center text-white text-sm font-medium shadow-md">
                {user?.firstName?.[0] || user?.name?.[0] || 'A'}
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate">{fullName}</p>
                <p className="text-xs text-dark-400 truncate">{roleLabel}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Header - Glassmorphism */}
        <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
          <div className="px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm font-medium text-slate-500">
                {/* Optional Breadcrumb area */}
              </div>
              <div className="flex items-center space-x-4">
                <DevAccountSwitcher />
                <BuildingSwitcher />
                <div className="relative group">
                  <button className="flex items-center space-x-2 focus:outline-none">
                    <span className="text-sm font-medium text-slate-700 hidden sm:block">
                      {user?.firstName || user?.name || 'Admin'}
                    </span>
                    <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-primary-600 text-sm font-semibold transition-transform group-hover:scale-105">
                      {user?.firstName?.[0] || user?.name?.[0] || 'A'}
                    </div>
                  </button>
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-right group-hover:scale-100 scale-95">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <div className="font-semibold text-slate-900">{fullName}</div>
                      <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                      {userRole && (
                        <div className="mt-1">
                          <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                            isAdmin ? 'bg-primary-100 text-primary-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {roleLabel}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-primary-600 transition-colors"
                      >
                        Profile Settings
                      </Link>
                    </div>
                    <div className="py-1 border-t border-slate-100">
                      <button
                        onClick={logout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="p-8 max-w-[1600px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
